/**
 * Target languages that carry the Atlas course track.
 * German and Swiss German stay on their existing sections.
 * English is included here as a course route only.
 */
import activeCourseTargets from '../../data/json/active-course-targets.json' with { type: 'json' };
import { courseLocaleFileComplete, scriptLocaleFileComplete } from './course-interface-readiness.js';
import { readCourseAudioStatuses } from './course-audio-status.js';
import { UI_LANGS } from '../../locale-registry.mjs';
import { mainCourseIsAvailable } from './main-course-availability.js';

export const COURSE_TARGET_LANGS = Object.freeze([
  "bg",
  "es",
  "cs",
  "da",
  "de",
  "et",
  "el",
  "en",
  "fr",
  "ga",
  "hr",
  "it",
  "lv",
  "lt",
  "hu",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "fi",
  "sv",
  "hi",
  "hy",
  "th",
  "ko",
  "sq", "ar", "zh", "ka", "id", "ja", "ne", "fa", "ru", "sw", "tr", "vi",
]);

/** Default language names; interface locales supply translated labels. */
export const COURSE_LANGUAGE_NAMES = Object.freeze({
  bg: "Bulgarian",
  es: "Spanish",
  cs: "Czech",
  da: "Danish",
  de: "German",
  et: "Estonian",
  el: "Greek",
  en: "English",
  fr: "French",
  ga: "Irish",
  hr: "Croatian",
  it: "Italian",
  lv: "Latvian",
  lt: "Lithuanian",
  hu: "Hungarian",
  mt: "Maltese",
  nl: "Dutch",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  sk: "Slovak",
  sl: "Slovenian",
  fi: "Finnish",
  sv: "Swedish",
  hi: "Hindi",
  hy: "Armenian",
  th: "Thai",
  sq: "Albanian",
  ar: "Arabic",
  zh: "Chinese",
  ka: "Georgian",
  id: "Indonesian",
  ja: "Japanese",
  ko: "Korean",
  ne: "Nepali",
  fa: "Persian",
  ru: "Russian",
  sw: "Swahili",
  tr: "Turkish",
  vi: "Vietnamese",
});

/** English course slugs. Each interface language overrides these from its course-slugs locale file. */
export const COURSE_TARGET_SLUGS = Object.freeze({
  bg: "bulgarian",
  es: "spanish",
  cs: "czech",
  da: "danish",
  et: "estonian",
  el: "greek",
  fr: "french",
  ga: "irish",
  hr: "croatian",
  it: "italian",
  lv: "latvian",
  lt: "lithuanian",
  hu: "hungarian",
  mt: "maltese",
  nl: "dutch",
  pl: "polish",
  pt: "portuguese",
  ro: "romanian",
  sk: "slovak",
  sl: "slovenian",
  fi: "finnish",
  sv: "swedish",
  hi: "hindi",
  hy: "armenian",
  th: "thai",
  sq: "albanian",
  ar: "arabic",
  zh: "chinese",
  ka: "georgian",
  id: "indonesian",
  ja: "japanese",
  ko: "korean",
  ne: "nepali",
  fa: "persian",
  ru: "russian",
  sw: "swahili",
  tr: "turkish",
  vi: "vietnamese",
});

export const COURSE_TARGET_LANDING = Object.freeze(
  Object.fromEntries(Object.entries(COURSE_TARGET_SLUGS).map(([code, slug]) => [code, `learn-${slug}`])),
);

const COURSE_TARGET_SET = new Set(COURSE_TARGET_LANGS);
const PUBLIC_TARGET_LANGS = Object.freeze(["de", "ch", "es"]);
const PUBLIC_COURSE_TARGETS = new Set();

/**
 * Courses that have been built, in homepage order.
 * The public homepage and learn menu use {@link visibleCourseTargetLangs}.
 */
export const ACTIVE_COURSE_TARGET_LANGS = Object.freeze([...activeCourseTargets]);

/**
 * Thai, Korean, Armenian and Hindi ship in production even if a later audio
 * audit marks the spoken inventory incomplete. They are script courses.
 */
export const PRODUCTION_SCRIPT_COURSE_LANGS = Object.freeze(["th", "ko", "hy", "hi"]);

export function isCourseTarget(code) {
  return COURSE_TARGET_SET.has(String(code || "").trim().toLowerCase());
}

export function courseLanguageName(code, uiLang = "en") {
  const c = String(code || "").trim().toLowerCase();
  if (uiLang === "de" && COURSE_LANGUAGE_NAMES[c]) return new Intl.DisplayNames(["de"], { type: "language" }).of(c);
  return COURSE_LANGUAGE_NAMES[c] || "";
}

/**
 * Production builds list only courses whose audio is complete.
 * Dev servers list every built course, including ones still missing audio.
 */
export function courseListingIsFiltered() {
  const nodeEnv = process.env["NODE_ENV"];
  const filtered = nodeEnv === "production";
  return filtered;
}

/** New courses are part of the site. Which ones are linked is {@link courseIsPublic}. */
export function showNewTargetLangs() {
  return true;
}

/** The recording inventory reports every required clip on disk. */
export function courseAudioIsComplete(code) {
  const row = readCourseAudioStatuses()[String(code || "").trim().toLowerCase()];
  return row?.status === "complete";
}

/**
 * Dev lists every catalogue course, including ones that have no audio yet.
 * Production lists a course only when its main-course audio inventory is complete.
 */
export function courseIsPublic(code) {
  const c = String(code || "").trim().toLowerCase();
  if (!isCourseTarget(c) || !mainCourseIsAvailable(c)) return false;
  if (!courseListingIsFiltered()) return true;
  if (!ACTIVE_COURSE_TARGET_LANGS.includes(c)) return false;
  if (PRODUCTION_SCRIPT_COURSE_LANGS.includes(c)) return true;
  return courseAudioIsComplete(c);
}

/** Homepage buttons and, with Swiss German, the learn menu. */
export function visibleCourseTargetLangs() {
  const source = courseListingIsFiltered() ? ACTIVE_COURSE_TARGET_LANGS : COURSE_TARGET_LANGS;
  return Object.freeze(source.filter((code) => courseIsPublic(code)));
}

/** Learn menu: German and Swiss German, plus every course this server lists. */
export function getNavTargetLangs() {
  const codes = ["de", "ch"];
  for (const code of visibleCourseTargetLangs()) {
    if (!codes.includes(code)) codes.push(code);
  }
  return Object.freeze(codes);
}

/**
 * Target languages that have a survival guide, and its page in each interface language.
 * Every path needs matching route folders under src/routes; the first interface is the x-default.
 */
const SURVIVAL_GUIDE_PATHS = Object.freeze({
  // BEGIN ISOLATED SURVIVAL COURSE BATCH
  it: Object.freeze({"en":"/en/italian/phrases","de":"/italienisch/phrasen","ar":"/ar/italiyya/ibarat","tr":"/tr/italyanca/ifadeler","uk":"/uk/italiiska/frazy","ru":"/ru/italyanskiy/frazy","es":"/es/italiano/frases","vi":"/vi/tieng-y/cau-giao-tiep","pl":"/pl/wloski/zwroty","el":"/el/italika/fraseis","fr":"/fr/italien/phrases","sl":"/sl/italijanscina/fraze"}),
  lt: Object.freeze({"en":"/en/lithuanian/phrases","de":"/litauisch/phrasen","ar":"/ar/lituaniyya/ibarat","tr":"/tr/litvanca/ifadeler","uk":"/uk/lytovska/frazy","ru":"/ru/litovskiy/frazy","es":"/es/lituano/frases","vi":"/vi/tieng-litva/cau-giao-tiep","pl":"/pl/litewski/zwroty","el":"/el/lithouanika/fraseis","fr":"/fr/lituanien/phrases","sl":"/sl/litovscina/fraze"}),
  tr: Object.freeze({"en":"/en/turkish/phrases","de":"/tuerkisch/phrasen","ar":"/ar/turkiyya/ibarat","tr":"/tr/turkce/ifadeler","uk":"/uk/turetska/frazy","ru":"/ru/turetskiy/frazy","es":"/es/turco/frases","vi":"/vi/tieng-tho-nhi-ky/cau-giao-tiep","pl":"/pl/turecki/zwroty","el":"/el/tourkika/fraseis","fr":"/fr/turc/phrases","sl":"/sl/turscina/fraze"}),
  // END ISOLATED SURVIVAL COURSE BATCH
  mt: Object.freeze({ en: "/en/maltese/phrases" }),
  en: Object.freeze({
    en: "/en/english/phrases",
    fr: "/fr/anglais/phrases",
    es: "/es/ingles/frases",
    sl: "/sl/anglescina/fraze",
    de: "/englisch/phrasen",
    ar: "/ar/ingliziyya/ibarat",
    tr: "/tr/ingilizce/ifadeler",
    el: "/el/anglika/fraseis",
    vi: "/vi/tieng-anh/cau-giao-tiep",
    pl: "/pl/angielski/zwroty",
    ru: "/ru/angliyskiy/frazy",
    uk: "/uk/anhliiska/frazy",
  }),
  de: Object.freeze({
    en: "/en/german/phrases",
    fr: "/fr/allemand/phrases",
    es: "/es/aleman/frases",
    sl: "/sl/nemscina/fraze",
    de: "/deutsch/phrasen",
    ar: "/ar/almaniyya/ibarat",
    tr: "/tr/almanca/ifadeler",
    el: "/el/germanika/fraseis",
    vi: "/vi/tieng-duc/cau-giao-tiep",
    pl: "/pl/niemiecki/zwroty",
    ru: "/ru/nemetskiy/frazy",
    uk: "/uk/nimetska/frazy",
  }),
});
export const SURVIVAL_GUIDE_TARGETS = Object.freeze(Object.keys(SURVIVAL_GUIDE_PATHS));

/** Interface languages a guide is written for. */
export function survivalGuideInterfaces(code) {
  return Object.keys(SURVIVAL_GUIDE_PATHS[String(code || "").trim().toLowerCase()] || {});
}

/** The guide in the visitor's interface language, else its first (English) version. */
export function survivalGuidePath(code, uiLang = "") {
  const paths = SURVIVAL_GUIDE_PATHS[String(code || "").trim().toLowerCase()];
  if (!paths) return "";
  return paths[String(uiLang || "").trim().toLowerCase()] || Object.values(paths)[0];
}

/** Published guide bases that now redirect to the canonical guide. */
export function legacySurvivalGuidePaths(code, uiLang) {
  if (code === "tr" && uiLang === "de") return ["/turkisch/phrasen"];
  return code === "mt" && uiLang === "en" ? ["/en/maltese/survival-phrases"] : [];
}

/** `1` publishes every built guide. A list such as `de` publishes those targets only. */
function publishedSurvivalGuides() {
  return new Set(String(process.env.LL_SURVIVAL_GUIDE_PUBLIC || "")
    .split(/[,\s]+/)
    .map((code) => code.trim().toLowerCase())
    .filter(Boolean));
}

/**
 * Dev links every built guide. Production links a guide when
 * LL_SURVIVAL_GUIDE_PUBLIC names it (`de`) or is `1`. Until then the pages answer 404.
 */
export function survivalGuideIsPublic(code) {
  if (!survivalGuidePath(code)) return false;
  if (!courseListingIsFiltered()) return true;
  const published = publishedSurvivalGuides();
  if (published.has("1") || published.has("*") || published.has("all")) return true;
  return published.has(String(code || "").trim().toLowerCase());
}

export function publicSurvivalGuideTargets() {
  return Object.freeze(SURVIVAL_GUIDE_TARGETS.filter((code) => survivalGuideIsPublic(code)));
}

export function firstConversationIsPublic(code = "mt") {
  return survivalGuideIsPublic(code);
}

/** Language landings for an unfinished course stay out of search. German, Swiss German, and Spanish story landings stay public. */
export function coursePagesAreHidden(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_TARGET_LANGS.includes(code)) return false;
  if (!isCourseTarget(code)) return false;
  return !courseIsPublic(code);
}

/** Course sections stay off menus, out of the sitemap, and noindex until the course is public. Direct URLs still render. */
export function courseSectionIsUnlisted(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (PUBLIC_COURSE_TARGETS.has(code)) return false;
  if (!isCourseTarget(code)) return false;
  return !courseIsPublic(code);
}

/** Main-course interfaces are independent of unfinished script translations. */
const GERMAN_PHASE_TARGETS = COURSE_TARGET_LANGS;
const EN_ONLY = Object.freeze(["en"]), EN_DE = Object.freeze(["en", "de"]);
const interfaceRenderCache = new Map();
const publishedInterfaceCache = new Map();
function interfacesForRender(target, script = false) {
  const key = `${target}:${script}`;
  // Production snapshots are immutable for the lifetime of a build/server.
  if (process.env.NODE_ENV === "production" && publishedInterfaceCache.has(key)) return publishedInterfaceCache.get(key);
  if (interfaceRenderCache.has(key)) return interfaceRenderCache.get(key);
  const interfaces = ["de", "en", "it", "lt", "tr"].includes(target) && !script
    ? Object.freeze(UI_LANGS.filter(ui => courseLocaleFileComplete(target, ui)))
    : (script ? scriptLocaleFileComplete : courseLocaleFileComplete)(target, "de") ? EN_DE : EN_ONLY;
  if (process.env.NODE_ENV === "production") publishedInterfaceCache.set(key, interfaces);
  interfaceRenderCache.set(key, interfaces);
  // A render generates hundreds of links. Reuse its decision until the next
  // microtask; the readiness helper itself always checks every dependency.
  queueMicrotask(() => interfaceRenderCache.delete(key));
  return interfaces;
}
export const COURSE_INTERFACE_LANGS = Object.freeze(Object.defineProperties({}, Object.fromEntries(
  GERMAN_PHASE_TARGETS.map(target => [target, { enumerable: true, get: () => interfacesForRender(target) }]),
)));
export const courseInterfaceLanguages = target => COURSE_INTERFACE_LANGS[target] || EN_ONLY;
export const courseInterfaceLanguage = (ui, target) => courseInterfaceLanguages(target).includes(ui) ? ui : "en";
export const scriptInterfaceLanguages = target => interfacesForRender(target, true);
export const scriptInterfaceLanguage = (ui, target) => scriptInterfaceLanguages(target).includes(ui) ? ui : "en";

/** Plain map for the browser nav. The client must not read locale files to choose a course URL. */
export function courseInterfaceLangMap() {
  return Object.fromEntries(COURSE_TARGET_LANGS.map(code => [code, courseInterfaceLanguages(code)]));
}

export function scriptInterfaceLangMap() {
  return Object.fromEntries(COURSE_TARGET_LANGS.map(code => [code, scriptInterfaceLanguages(code)]));
}

const ENGLISH_NOTES = Object.freeze({ ar: "بالإنجليزية", de: "auf Englisch", el: "στα αγγλικά", en: "in English", es: "en inglés", fr: "en anglais", pl: "po angielsku", ru: "на английском", sl: "v angleščini", tr: "İngilizce", uk: "англійською", vi: "bằng tiếng Anh" });

export const englishNote = uiLang => `(${ENGLISH_NOTES[uiLang] || ENGLISH_NOTES.en})`;

/** Landing buttons name the fallback interface in the visitor's language. */
export function courseButtonLabel(label, uiLang, targetCode, { script = false } = {}) {
  const text = String(label || "");
  const ui = String(uiLang || "").trim().toLowerCase();
  // German and English have localized main courses; their CTA stays concise
  // even while a build process is still preparing its packaged snapshots.
  if (!script && ["de", "en"].includes(String(targetCode || "").trim().toLowerCase())) return text;
  if (!text || (script ? scriptInterfaceLanguage : courseInterfaceLanguage)(ui, targetCode) === ui) return text;
  const suffix = `(${ENGLISH_NOTES[ui] || ENGLISH_NOTES.en})`;
  return text.endsWith(suffix) ? text : `${text} ${suffix}`;
}

export const scriptButtonLabel = (label, uiLang, targetCode) => courseButtonLabel(label, uiLang, targetCode, { script: true });
